/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { DndContext, useDndMonitor, useDraggable } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { Box, Grid, Paper, Skeleton, Stack, useTheme } from '@mui/material';
import get from 'lodash-es/get';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { LEFT_NAV_WIDTH, SUB_NAVIGATION_HEIGHT, TOP_NAVIGATION_HEIGHT } from '../../constants/style';
import useContract from '../../hooks/useContract';
import { ContractConfigurationContext, ContractContextType } from '../../hooks/useContractConfigurationContext';
import useLineItems from '../../hooks/useLineItems';
import oneDspTheme from '../../theme/oneDspTheme';
import LineItemNav from '../layout/ContractNav';

interface SideBarProps {
  setCurrentLeftNavWidth: Function;
}

// Need to add a delay or else we get a ResizeObserver error
const DRAG_DELAY = 5;

function SideBar({ setCurrentLeftNavWidth }: SideBarProps) {
  const [originLeftNavWidth, setOriginLeftNavWidth] = useState(LEFT_NAV_WIDTH);
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: 'side-menu-resize',
  });

  const style = {
    transform: CSS.Transform.toString(transform),
  };

  useDndMonitor({
    onDragMove: (e) => {
      setTimeout(() => {
        const newOriginLeftNavWidth = originLeftNavWidth + e.delta.x;
        setCurrentLeftNavWidth(newOriginLeftNavWidth);
      }, DRAG_DELAY);
    },
    onDragEnd: (e) => {
      const newOriginLeftNavWidth = originLeftNavWidth + e.delta.x;
      setCurrentLeftNavWidth(newOriginLeftNavWidth);
      setOriginLeftNavWidth(newOriginLeftNavWidth);
    },
  });

  return (
    <Stack
      sx={{
        position: 'absolute',
        left: `${originLeftNavWidth}px`,
        top: 0,
        width: '10px',
        height: '100%',
        zIndex: 100,
        cursor: 'ew-resize',
        '&:hover': {
          borderLeft: `2px solid ${oneDspTheme.palette.primary.main}`,
        },
        ...style,
      }}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
    />
  );
}

function ContractConfiguration() {
  const params = useParams();
  const contractId = Number(params.id) || -1;
  // Pass in -1 as contract to get a new contract.
  const { isLoading, refetch: refetchDelayed, data, isError } = useContract(contractId);
  const [contractName, setContractName] = useState(data?.name);
  const lineItems = useLineItems(Number(params.id));
  const [currentLeftNavWidth, setCurrentLeftNavWidth] = useState(LEFT_NAV_WIDTH);
  const [isRefetching, setIsRefetching] = useState(false);
  const [onToggleAdGroupPauseCallback, setOnToggleAdGroupPauseCallback] = useState();
  const theme = useTheme();
  const [mainBodyWidth, setMainBodyWidth] = useState(0);

  // eslint-disable-next-line @typescript-eslint/dot-notation
  const mainNavHeight = theme?.mixins?.toolbar?.['@media (min-width:600px)']?.['minHeight'] || TOP_NAVIGATION_HEIGHT;
  const offsetBothNavs = mainNavHeight + SUB_NAVIGATION_HEIGHT;
  const hasUnpausedCampaigns = get(data, 'has_unpaused_campaigns');

  useEffect(() => {
    if (data && data.name) {
      setContractName(data.name);
    }
  }, [data]);

  /*
  // TODO: seems to be fixed; remove this code in 6 months from Dec 12, 2024 if we do not the ResizeObserver overlay error
  // Workaround to hide the error, we need to fix it.
  // Based on https://stackoverflow.com/a/78597630/2530148
  useEffect(() => {
    function hideError(error) {
      if (error.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const iframes = document.getElementsByTagName('iframe');
        if (iframes.length > 0) {
          Array.from(iframes).forEach((iframe) => {
            const iframeId = iframe.getAttribute('id');
            if (iframeId === 'webpack-dev-server-client-overlay') {
              // eslint-disable-next-line no-param-reassign
              iframe.style.display = 'none';
            }
          });
        }
      }
    }

    window.addEventListener('error', hideError);
    return () => {
      window.addEventListener('error', hideError);
    };
  }, []);
  */

  const mainBodyRef = useCallback(
    (node) => {
      const observer = new ResizeObserver(() => {
        setMainBodyWidth(node.offsetWidth || 0);
      });
      if (node) {
        observer.observe(node);
      }
      return () => node && observer.unobserve(node);
    },
    [setMainBodyWidth]
  );

  const value: ContractContextType = useMemo(
    () => ({
      contractData: data,
      contractName,
      setContractName,
      lineItemsData: lineItems?.data?.collection,
      hasUnpausedCampaigns,
      isRefetching: isLoading || isRefetching,
      onToggleAdGroupPauseCallback,
      setOnToggleAdGroupPauseCallback,
      // There is some latency on the server when updating the paused status for all campaigns.
      refetch: () => {
        setIsRefetching(true);
        setTimeout(() => {
          refetchDelayed();
          setIsRefetching(false);
        }, 3000);
      },
      mainBodyWidth,
      isError,
    }),
    [
      data,
      lineItems.data,
      refetchDelayed,
      isLoading,
      isRefetching,
      hasUnpausedCampaigns,
      onToggleAdGroupPauseCallback,
      setOnToggleAdGroupPauseCallback,
      mainBodyWidth,
      isError,
      contractName,
      setContractName,
    ]
  );

  if (isLoading) {
    return (
      <Grid container>
        <Grid item xs={3}>
          <Skeleton variant="rectangular" width="100%" height="92vh" />
        </Grid>
        <Grid item xs={9}>
          <Skeleton sx={{ m: 2 }} variant="rectangular" width="25%" height="25" />
          <Skeleton sx={{ m: 2 }} variant="rectangular" width="95%" height="25" />
          <Skeleton sx={{ m: 2 }} variant="rectangular" width="50%" height="25" />
          <Skeleton sx={{ m: 2 }} variant="rectangular" width="95%" height="85vh" />
        </Grid>
      </Grid>
    );
  }

  return (
    <DndContext modifiers={[restrictToHorizontalAxis]}>
      <ContractConfigurationContext.Provider value={value}>
        <Box display="flex" sx={{ position: 'relative' }}>
          <Paper
            sx={{
              position: 'absolute',
              height: import.meta.env.VITE_IS_FEDERATED
                ? `calc(100vh - ${offsetBothNavs}px)`
                : `calc(100vh - ${SUB_NAVIGATION_HEIGHT}px)`,
              overflowY: 'auto',
              width: currentLeftNavWidth,
              borderColor: theme.palette.grey[300],
              borderRadius: 0,
            }}
          >
            <LineItemNav contract={data} />
          </Paper>
          <SideBar setCurrentLeftNavWidth={setCurrentLeftNavWidth} />
          <Box
            component="main"
            sx={{
              position: 'relative',
              width: '100%',
              height: import.meta.env.VITE_IS_FEDERATED
                ? `calc(100vh - ${offsetBothNavs}px)`
                : `calc(100vh - ${SUB_NAVIGATION_HEIGHT}px)`,
              marginLeft: `${currentLeftNavWidth + 2}px`,
              overflowY: 'auto',
            }}
            id="config-main-content"
            ref={mainBodyRef}
          >
            <Outlet />
          </Box>
        </Box>
      </ContractConfigurationContext.Provider>
    </DndContext>
  );
}

export default ContractConfiguration;
